import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { setData } from 'platform/forms-system/src/js/actions';
import { scrollAndFocus } from 'platform/utilities/scroll';
import {
  VaComboBox,
  VaMemorableDate,
  VaSelect,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { formatIsoDate, sanitize } from '../domain/helpers';
import {
  PAY_GRADE_OPTIONS,
  CHARACTER_OF_SERVICE_OPTIONS,
  SEPARATION_CODES,
} from '../domain/constants';
import { entryHasRequiredMissing } from '../domain/artifacts';
import { validateIsoDate } from '../domain/validators';

// ---------------------------------------------------------------------------
// Section keys — only non-fieldMapping fields remain
// ---------------------------------------------------------------------------
const DD214_SERVICE = 'dd214-service';
const DEATH_DEATH_INFO = 'deathCert-deathInfo';

const sectionKey = (artifactType, fileIndex, entryIndex, section) =>
  `${artifactType}-${fileIndex}-${entryIndex}-${section}`;

// Field-level required error messages — "Enter a …" for text inputs,
// "Select a …" for selects/combos, "Provide …" for date fields.
const REQUIRED_MESSAGES = {
  gradeRateRank: 'Enter a grade, rate, or rank',
  payGrade: 'Select a pay grade',
  dateInducted: 'Enter the date inducted',
  causeOfSeparation: 'Enter a cause of separation',
  separationType: 'Enter a separation type',
  separationCode: 'Select a separation code',
  characterOfService: 'Select a character of service',
  decendentDateOfDisposition: 'Enter the disposition date',
  causeOfDeath: 'Enter a cause of death',
  mannerOfDeath: 'Enter a manner of death',
  decendentMaritalStatus: 'Enter a marital status',
};

// ---------------------------------------------------------------------------
// Draft extraction helpers
// ---------------------------------------------------------------------------
const extractDraft = (entry, section) => {
  if (section === DD214_SERVICE) {
    return {
      gradeRateRank: entry.gradeRateRank ?? '',
      payGrade: entry.payGrade ?? '',
      dateInducted: entry.dateInducted ?? '',
      causeOfSeparation: entry.causeOfSeparation ?? '',
      separationType: entry.separationType ?? '',
      separationCode: entry.separationCode ?? '',
      characterOfService: entry.characterOfService ?? '',
    };
  }
  if (section === DEATH_DEATH_INFO) {
    return {
      decendentDateOfDisposition: entry.decendentDateOfDisposition ?? '',
      causeOfDeath: entry.causeOfDeath ?? '',
      underlyingCauseOfDeathB: entry.underlyingCauseOfDeathB ?? '',
      underlyingCauseOfDeathC: entry.underlyingCauseOfDeathC ?? '',
      underlyingCauseOfDeathD: entry.underlyingCauseOfDeathD ?? '',
      mannerOfDeath: entry.mannerOfDeath ?? '',
      decendentMaritalStatus: entry.decendentMaritalStatus ?? '',
    };
  }
  return {};
};

// ---------------------------------------------------------------------------
// Validation — required fields get specific messages; optional B/C/D fields
// are skipped if empty; text values are truncated at 1000 chars via maxlength
// on the input so no length error is needed here.
// ---------------------------------------------------------------------------
const validateDraft = (draft, section) => {
  const errs = {};

  if (section === DD214_SERVICE) {
    [
      'gradeRateRank',
      'causeOfSeparation',
      'payGrade',
      'separationType',
      'separationCode',
      'characterOfService',
    ].forEach(field => {
      if (!draft[field]) errs[field] = REQUIRED_MESSAGES[field];
    });

    const dateErr = validateIsoDate(draft.dateInducted, {
      required: REQUIRED_MESSAGES.dateInducted,
    });
    if (dateErr) errs.dateInducted = dateErr;
  }

  if (section === DEATH_DEATH_INFO) {
    const dateErr = validateIsoDate(draft.decendentDateOfDisposition, {
      required: REQUIRED_MESSAGES.decendentDateOfDisposition,
    });
    if (dateErr) errs.decendentDateOfDisposition = dateErr;

    ['causeOfDeath', 'mannerOfDeath', 'decendentMaritalStatus'].forEach(
      field => {
        if (!draft[field]) errs[field] = REQUIRED_MESSAGES[field];
      },
    );
    // B/C/D are optional — no validation when empty
  }

  return errs;
};

// Truncate text values to 1000 chars before storing
const truncateDraft = draft =>
  Object.fromEntries(
    Object.entries(draft).map(([k, v]) => [
      k,
      typeof v === 'string' && v.length > 1000 ? v.slice(0, 1000) : v,
    ]),
  );

// Draft keys that hold ISO dates and should be zero-padded when finalized.
const DATE_FIELDS = ['dateInducted', 'decendentDateOfDisposition'];

// Zero-pad the month and day of a raw `YYYY-M-D` string to `YYYY-MM-DD`.
// Empty segments are left empty so partial values stay detectable by
// validation; a fully empty value normalizes to ''.
const padIsoDate = value => {
  if (typeof value !== 'string' || value === '') return value;
  const [year = '', month = '', day = ''] = value.split('-');
  const parts = [
    year,
    month ? month.padStart(2, '0') : '',
    day ? day.padStart(2, '0') : '',
  ];
  return parts.every(part => part === '') ? '' : parts.join('-');
};

// ---------------------------------------------------------------------------
// Read-only row — styled like the review/submit page with separator lines.
// Optional rows are hidden entirely when empty.
// Required rows show italic "Missing" when empty.
// ---------------------------------------------------------------------------
const ReadRow = ({ label, value, optional, showError }) => {
  const hasError = !value && !optional && showError;

  let valueNode;
  if (hasError) {
    valueNode = <span className="vads-u-color--secondary-dark">Missing</span>;
  } else {
    valueNode = sanitize(value);
  }

  return (
    <div
      className={
        hasError
          ? 'vads-u-border-left--5px vads-u-border-color--secondary-dark'
          : ''
      }
    >
      <div className="vads-u-border-bottom--1px vads-u-border-color--gray-light vads-u-padding-y--1p5 vads-u-display--flex vads-u-justify-content--space-between">
        <dt
          className={`vads-u-margin--0 vads-u-font-weight--normal ${
            hasError
              ? 'vads-u-color--secondary-dark vads-u-padding-left--1'
              : 'vads-u-color--gray-dark'
          }`}
        >
          {label}
        </dt>
        <dd className="dd-privacy-mask vads-u-margin--0 vads-u-text-align--right vads-u-font-weight--bold">
          {valueNode}
        </dd>
      </div>
    </div>
  );
};

ReadRow.propTypes = {
  label: PropTypes.string,
  optional: PropTypes.bool,
  showError: PropTypes.bool,
  value: PropTypes.node,
};

// ---------------------------------------------------------------------------
// Edit form inputs
// ---------------------------------------------------------------------------
const TextField = ({ field, label, value, error, onInput, required }) => (
  <div className="vads-u-margin-bottom--3">
    <va-text-input
      label={label}
      name={field}
      value={value || ''}
      error={error || null}
      maxlength="1000"
      required={required || null}
      onInput={e => onInput(field, e.target.value)}
      uswds
    />
  </div>
);

TextField.propTypes = {
  error: PropTypes.string,
  field: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onInput: PropTypes.func,
};

const SelectField = ({
  field,
  label,
  value,
  error,
  options,
  onChange,
  required,
}) => (
  <div className="vads-u-margin-bottom--3">
    <VaSelect
      label={label}
      name={field}
      value={value || ''}
      error={error || null}
      required={required || null}
      onVaSelect={e => onChange(field, e.detail.value)}
      uswds
    >
      {options.map(opt =>
        typeof opt === 'string' ? (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ) : (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ),
      )}
    </VaSelect>
  </div>
);

SelectField.propTypes = {
  error: PropTypes.string,
  field: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

const DateField = ({
  field,
  label,
  value,
  error,
  onDateChange,
  onBlur,
  required,
}) => (
  <div className="vads-u-margin-bottom--3">
    <VaMemorableDate
      label={label}
      name={field}
      value={value || ''}
      error={error || null}
      onDateChange={e => {
        // Store the value exactly as entered (including partial/blank
        // segments) so controlled re-renders don't repopulate cleared fields.
        // Month/day are zero-padded later, on blur, to avoid corrupting
        // in-progress typing (e.g. a lone "0" becoming "00").
        const raw = e.target?.value ?? '';
        onDateChange(field, raw === '--' ? '' : raw);
      }}
      onDateBlur={() => onBlur?.(field)}
      uswds
      required={required || null}
      externalValidation
    />
  </div>
);

DateField.propTypes = {
  error: PropTypes.string,
  field: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onBlur: PropTypes.func,
  onDateChange: PropTypes.func,
};

const ComboField = ({
  field,
  label,
  value,
  error,
  options,
  onChange,
  required,
}) => (
  <div className="vads-u-margin-bottom--3">
    <VaComboBox
      label={label}
      name={field}
      value={value || ''}
      error={error || null}
      required={required || null}
      onVaSelect={e => onChange(field, e.detail.value)}
      uswds
    >
      {options.map(opt => {
        const v = typeof opt === 'string' ? opt : opt.value;
        const l = typeof opt === 'string' ? opt : opt.label;
        return (
          <option key={v} value={v}>
            {l}
          </option>
        );
      })}
    </VaComboBox>
  </div>
);

ComboField.propTypes = {
  error: PropTypes.string,
  field: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

// ---------------------------------------------------------------------------
// Section component
// ---------------------------------------------------------------------------
const Section = ({
  heading,
  isEditing,
  children,
  readRows,
  showError,
  onEdit,
  onSave,
  onCancel,
}) => (
  <div className="vads-u-margin-bottom--3">
    <div className="vads-u-display--flex vads-u-justify-content--space-between vads-u-align-items--center vads-u-margin-bottom--1">
      <h5 className="vads-u-font-size--md vads-u-margin--0">{heading}</h5>
      {!isEditing && (
        <va-button
          secondary
          label={`Edit ${heading}`}
          onClick={onEdit}
          text="Edit"
          uswds
          data-dd-action-name="Edit Artifact Section"
        />
      )}
    </div>

    {isEditing ? (
      <div>
        {children}
        <div className="vads-u-margin-top--2 vads-u-display--flex vads-u-flex-direction--row">
          <va-button
            onClick={onSave}
            text="Save"
            uswds
            data-dd-action-name="Save Artifact Section"
          />
          <va-button
            secondary
            onClick={onCancel}
            text="Cancel"
            uswds
            class="vads-u-margin-left--1"
            data-dd-action-name="Cancel Artifact Section"
          />
        </div>
      </div>
    ) : (
      <dl className="vads-u-border-top--1px vads-u-border-color--gray-light vads-u-margin--0 vads-u-padding--0">
        {readRows.map(row => (
          <ReadRow
            key={row.label}
            label={row.label}
            value={row.value}
            optional={row.optional}
            showError={showError}
          />
        ))}
      </dl>
    )}
  </div>
);

Section.propTypes = {
  children: PropTypes.node,
  heading: PropTypes.string,
  isEditing: PropTypes.bool,
  readRows: PropTypes.array,
  showError: PropTypes.bool,
  onCancel: PropTypes.func,
  onEdit: PropTypes.func,
  onSave: PropTypes.func,
};

// ---------------------------------------------------------------------------
// Item wrapper — renders each artifact either as a flat block (default, used
// on the review-and-submit page) or as a bordered accordion item (used on the
// standalone in-flow review page). The missing-information indicator and the
// `data-has-errors` anchor (scroll-to/focus target) live on the wrapper so both
// layouts behave the same for validation.
// ---------------------------------------------------------------------------
const ItemWrapper = ({
  variant,
  headline,
  subhead,
  showItemError,
  children,
}) => {
  if (variant === 'accordion') {
    return (
      <va-accordion-item
        header={headline}
        subheader={
          showItemError
            ? `${subhead} Some information is missing. Please review.`
            : subhead
        }
        data-has-errors={showItemError ? 'true' : undefined}
        open
        bordered
        uswds
      >
        {showItemError && (
          <va-icon slot="icon" icon="error" class="vads-u-color--secondary" />
        )}
        {children}
      </va-accordion-item>
    );
  }

  return (
    <div
      className="vads-u-margin-bottom--4"
      data-has-errors={showItemError ? 'true' : undefined}
    >
      <h4 className="vads-u-font-size--h4 vads-u-margin-top--0 vads-u-margin-bottom--1">
        {showItemError && (
          <va-icon
            icon="error"
            class="vads-u-color--secondary vads-u-margin-right--1"
          />
        )}
        {headline}
      </h4>
      {showItemError && (
        <p className="vads-u-color--secondary vads-u-margin-top--0">
          {subhead} Some information is missing. Please review.
        </p>
      )}
      {!showItemError && <p className="vads-u-margin-top--0">{subhead}</p>}
      {children}
    </div>
  );
};

ItemWrapper.propTypes = {
  children: PropTypes.node,
  headline: PropTypes.string,
  showItemError: PropTypes.bool,
  subhead: PropTypes.string,
  variant: PropTypes.string,
};

// ---------------------------------------------------------------------------
// DD-214 item
// Fields in fieldMapping (name, SSN, DOB, branch, service entry/separation
// dates) are omitted — they're shown in the conflict-resolution sections.
// ---------------------------------------------------------------------------
const Dd214Item = ({
  headline,
  entry,
  fileIndex,
  entryIndex,
  editingSection,
  draftValues,
  errors,
  showErrors,
  onEdit,
  onSave,
  onCancel,
  onField,
  onDate,
  onBlur,
  variant,
}) => {
  const serviceKey = sectionKey('dd214', fileIndex, entryIndex, DD214_SERVICE);
  const hasErrors = entryHasRequiredMissing(entry, 'dd214');
  const showItemError = showErrors && hasErrors;

  return (
    <ItemWrapper
      variant={variant}
      headline={headline}
      subhead="The information should appear exactly as written on the document."
      showItemError={showItemError}
    >
      <Section
        heading="Veteran’s service information"
        isEditing={editingSection === serviceKey}
        onEdit={() => onEdit(serviceKey, entry, DD214_SERVICE)}
        onSave={() => onSave(serviceKey, fileIndex, entryIndex, 'dd214')}
        onCancel={onCancel}
        showError={showErrors}
        readRows={[
          { label: 'Grade, rate, or rank', value: entry.gradeRateRank },
          { label: 'Pay grade', value: entry.payGrade },
          {
            label: 'Date inducted',
            value: entry.dateInducted
              ? formatIsoDate(entry.dateInducted)
              : null,
          },
          { label: 'Cause of separation', value: entry.causeOfSeparation },
          { label: 'Separation type', value: entry.separationType },
          { label: 'Separation code', value: entry.separationCode },
          { label: 'Character of service', value: entry.characterOfService },
        ]}
      >
        <TextField
          field="gradeRateRank"
          label="Grade, rate, or rank"
          value={draftValues.gradeRateRank}
          error={errors.gradeRateRank}
          onInput={onField}
          required
        />
        <ComboField
          field="payGrade"
          label="Pay grade"
          value={draftValues.payGrade}
          error={errors.payGrade}
          options={PAY_GRADE_OPTIONS}
          onChange={onField}
          required
        />
        <DateField
          field="dateInducted"
          label="Date inducted"
          value={draftValues.dateInducted}
          error={errors.dateInducted}
          onDateChange={onDate}
          onBlur={onBlur}
          required
        />
        <TextField
          field="causeOfSeparation"
          label="Cause of separation"
          value={draftValues.causeOfSeparation}
          error={errors.causeOfSeparation}
          onInput={onField}
          required
        />
        <TextField
          field="separationType"
          label="Separation type"
          value={draftValues.separationType}
          error={errors.separationType}
          onInput={onField}
          required
        />
        <ComboField
          field="separationCode"
          label="Separation code"
          value={draftValues.separationCode}
          error={errors.separationCode}
          options={SEPARATION_CODES}
          onChange={onField}
          required
        />
        <SelectField
          field="characterOfService"
          label="Character of service"
          value={draftValues.characterOfService}
          error={errors.characterOfService}
          options={CHARACTER_OF_SERVICE_OPTIONS}
          onChange={onField}
          required
        />
      </Section>
    </ItemWrapper>
  );
};

Dd214Item.propTypes = {
  draftValues: PropTypes.object,
  editingSection: PropTypes.string,
  entry: PropTypes.object,
  entryIndex: PropTypes.number,
  errors: PropTypes.object,
  fileIndex: PropTypes.number,
  headline: PropTypes.string,
  showErrors: PropTypes.bool,
  variant: PropTypes.string,
  onBlur: PropTypes.func,
  onCancel: PropTypes.func,
  onDate: PropTypes.func,
  onEdit: PropTypes.func,
  onField: PropTypes.func,
  onSave: PropTypes.func,
};

// ---------------------------------------------------------------------------
// Death certificate item
// Fields in fieldMapping (name, SSN, date of death) are omitted.
// Cause of death B/C/D are optional per the death certificate standard.
// ---------------------------------------------------------------------------
const DeathCertItem = ({
  headline,
  entry,
  fileIndex,
  entryIndex,
  editingSection,
  draftValues,
  errors,
  showErrors,
  onEdit,
  onSave,
  onCancel,
  onField,
  onDate,
  onBlur,
  variant,
}) => {
  const deathInfoKey = sectionKey(
    'deathCert',
    fileIndex,
    entryIndex,
    DEATH_DEATH_INFO,
  );
  const hasErrors = entryHasRequiredMissing(entry, 'deathCertificates');
  const showItemError = showErrors && hasErrors;

  return (
    <ItemWrapper
      variant={variant}
      headline={headline}
      subhead="The information should appear exactly as written on the certificate, including all listed causes of death."
      showItemError={showItemError}
    >
      <Section
        heading="Death information"
        isEditing={editingSection === deathInfoKey}
        onEdit={() => onEdit(deathInfoKey, entry, DEATH_DEATH_INFO)}
        onSave={() =>
          onSave(deathInfoKey, fileIndex, entryIndex, 'deathCertificates')
        }
        onCancel={onCancel}
        showError={showErrors}
        readRows={[
          {
            label: 'Disposition date',
            value: entry.decendentDateOfDisposition
              ? formatIsoDate(entry.decendentDateOfDisposition)
              : null,
          },
          { label: 'Cause of death A', value: entry.causeOfDeath },
          {
            label: 'Cause of death B',
            value: entry.underlyingCauseOfDeathB,
            optional: true,
          },
          {
            label: 'Cause of death C',
            value: entry.underlyingCauseOfDeathC,
            optional: true,
          },
          {
            label: 'Cause of death D',
            value: entry.underlyingCauseOfDeathD,
            optional: true,
          },
          { label: 'Manner of death', value: entry.mannerOfDeath },
          {
            label: 'Marital status at time of death',
            value: entry.decendentMaritalStatus,
          },
        ]}
      >
        <DateField
          field="decendentDateOfDisposition"
          label="Disposition date"
          value={draftValues.decendentDateOfDisposition}
          error={errors.decendentDateOfDisposition}
          onDateChange={onDate}
          onBlur={onBlur}
          required
        />
        <TextField
          field="causeOfDeath"
          label="Cause of death A"
          value={draftValues.causeOfDeath}
          error={errors.causeOfDeath}
          onInput={onField}
          required
        />
        <TextField
          field="underlyingCauseOfDeathB"
          label="Cause of death B (if applicable)"
          value={draftValues.underlyingCauseOfDeathB}
          error={errors.underlyingCauseOfDeathB}
          onInput={onField}
        />
        <TextField
          field="underlyingCauseOfDeathC"
          label="Cause of death C (if applicable)"
          value={draftValues.underlyingCauseOfDeathC}
          error={errors.underlyingCauseOfDeathC}
          onInput={onField}
        />
        <TextField
          field="underlyingCauseOfDeathD"
          label="Cause of death D (if applicable)"
          value={draftValues.underlyingCauseOfDeathD}
          error={errors.underlyingCauseOfDeathD}
          onInput={onField}
        />
        <TextField
          field="mannerOfDeath"
          label="Manner of death"
          value={draftValues.mannerOfDeath}
          error={errors.mannerOfDeath}
          onInput={onField}
          required
        />
        <TextField
          field="decendentMaritalStatus"
          label="Marital status at time of death"
          value={draftValues.decendentMaritalStatus}
          error={errors.decendentMaritalStatus}
          onInput={onField}
          required
        />
      </Section>
    </ItemWrapper>
  );
};

DeathCertItem.propTypes = {
  draftValues: PropTypes.object,
  editingSection: PropTypes.string,
  entry: PropTypes.object,
  entryIndex: PropTypes.number,
  errors: PropTypes.object,
  fileIndex: PropTypes.number,
  headline: PropTypes.string,
  showErrors: PropTypes.bool,
  variant: PropTypes.string,
  onBlur: PropTypes.func,
  onCancel: PropTypes.func,
  onDate: PropTypes.func,
  onEdit: PropTypes.func,
  onField: PropTypes.func,
  onSave: PropTypes.func,
};

// ---------------------------------------------------------------------------
// Derive section constant from composite key
// ---------------------------------------------------------------------------
const sectionTypeFromKey = key => {
  if (key.endsWith(DD214_SERVICE)) return DD214_SERVICE;
  if (key.endsWith(DEATH_DEATH_INFO)) return DEATH_DEATH_INFO;
  return null;
};

// ---------------------------------------------------------------------------
// Main component
// Flat, embeddable summary of the CAVE-extracted document data. Iterates all
// uploaded files and renders one section per artifact (DD-214 / death
// certificate) with inline editing. Designed to be embedded in the upload
// page's review (CaveUploadReview) on the review-and-submit page; it reads and
// writes form data directly via redux and renders no navigation of its own.
// ---------------------------------------------------------------------------
const ArtifactSummary = ({ showErrors = true, variant = 'flat' }) => {
  const formData = useSelector(state => state?.form?.data ?? {});
  const dispatch = useDispatch();
  const files = formData.files ?? [];

  const [editingSection, setEditingSection] = useState(null);
  const [draftValues, setDraftValues] = useState({});
  const [errors, setErrors] = useState({});
  const rootRef = useRef(null);

  const handleEdit = (key, entry, section) => {
    setEditingSection(key);
    const draft = extractDraft(entry, section);
    setDraftValues(draft);
    // Surface any missing/invalid extracted values immediately on open;
    // blur then re-validates individual fields as the user edits.
    setErrors(validateDraft(draft, section));
  };

  const handleCancel = () => {
    setEditingSection(null);
    setDraftValues({});
    setErrors({});
  };

  const clearFieldError = field =>
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const handleField = (field, value) => {
    setDraftValues(prev => ({ ...prev, [field]: value }));
    if (value) clearFieldError(field);
  };

  const handleDate = (field, value) => {
    setDraftValues(prev => ({ ...prev, [field]: value }));
    if (value) clearFieldError(field);
  };

  // Validate a single field when it loses focus, leaving other fields' errors
  // (and untouched fields) alone. Date fields are zero-padded here, once the
  // user is done typing, before validating.
  const handleBlur = field => {
    const section = sectionTypeFromKey(editingSection);
    const paddedValue = padIsoDate(draftValues[field]);
    const nextDraft =
      paddedValue === draftValues[field]
        ? draftValues
        : { ...draftValues, [field]: paddedValue };
    if (nextDraft !== draftValues) setDraftValues(nextDraft);
    const fieldErrors = validateDraft(nextDraft, section);
    setErrors(prev => {
      const next = { ...prev };
      if (fieldErrors[field]) next[field] = fieldErrors[field];
      else delete next[field];
      return next;
    });
  };

  const handleSave = (key, fileIdx, entryIndex, artifactType) => {
    const section = sectionTypeFromKey(key);
    const errs = validateDraft(draftValues, section);
    if (Object.keys(errs).length) {
      setErrors(errs);
      // Move focus to the first control with an error after the re-render.
      setTimeout(() => {
        const root = rootRef.current;
        const firstError = Array.from(
          root?.querySelectorAll('[name]') ?? [],
        ).find(el => el.error || el.getAttribute('error'));
        if (firstError) scrollAndFocus(firstError);
      }, 0);
      return;
    }

    const truncated = truncateDraft(draftValues);
    // Guarantee stored dates are zero-padded ISO, even if a date field was
    // never blurred before saving.
    DATE_FIELDS.forEach(f => {
      if (truncated[f] != null) truncated[f] = padIsoDate(truncated[f]);
    });

    const updatedFiles = files.map((f, fi) => {
      if (fi !== fileIdx) return f;
      const artifacts = f.idpArtifacts ?? {};
      const entries = artifacts[artifactType] ?? [];
      const updatedEntries = entries.map((e, ei) =>
        ei === entryIndex ? { ...e, ...truncated } : e,
      );
      return {
        ...f,
        idpArtifacts: { ...artifacts, [artifactType]: updatedEntries },
      };
    });
    dispatch(setData({ ...formData, files: updatedFiles }));
    setEditingSection(null);
    setDraftValues({});
    setErrors({});
  };

  const totalDd214Count = files.reduce(
    (sum, f) => sum + (f?.idpArtifacts?.dd214?.length ?? 0),
    0,
  );
  const totalDeathCertCount = files.reduce(
    (sum, f) => sum + (f?.idpArtifacts?.deathCertificates?.length ?? 0),
    0,
  );

  const items = [];
  let dd214Counter = 0;
  let deathCertCounter = 0;

  files.forEach((file, fileIndex) => {
    const artifacts = file?.idpArtifacts;
    if (!artifacts) return;

    (artifacts.dd214 ?? []).forEach((entry, entryIndex) => {
      dd214Counter += 1;
      const dd214Headline =
        totalDd214Count > 1
          ? `DD214 or separation documents information (${dd214Counter})`
          : 'DD214 or separation documents information';
      items.push(
        <Dd214Item
          key={`dd214-${fileIndex}-${entryIndex}`}
          headline={dd214Headline}
          entry={entry}
          fileIndex={fileIndex}
          entryIndex={entryIndex}
          editingSection={editingSection}
          draftValues={draftValues}
          errors={errors}
          showErrors={showErrors}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={handleCancel}
          onField={handleField}
          onDate={handleDate}
          onBlur={handleBlur}
          variant={variant}
        />,
      );
    });

    (artifacts.deathCertificates ?? []).forEach((entry, entryIndex) => {
      deathCertCounter += 1;
      const deathCertHeadline =
        totalDeathCertCount > 1
          ? `Death certificate information (${deathCertCounter})`
          : 'Death certificate information';
      items.push(
        <DeathCertItem
          key={`deathCert-${fileIndex}-${entryIndex}`}
          headline={deathCertHeadline}
          entry={entry}
          fileIndex={fileIndex}
          entryIndex={entryIndex}
          editingSection={editingSection}
          draftValues={draftValues}
          errors={errors}
          showErrors={showErrors}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={handleCancel}
          onField={handleField}
          onDate={handleDate}
          onBlur={handleBlur}
          variant={variant}
        />,
      );
    });
  });

  if (!items.length) {
    return null;
  }

  if (variant === 'accordion') {
    return (
      <va-accordion ref={rootRef} uswds>
        {items}
      </va-accordion>
    );
  }

  return (
    <div ref={rootRef} className="vads-u-width--full">
      {items}
    </div>
  );
};

ArtifactSummary.propTypes = {
  showErrors: PropTypes.bool,
  variant: PropTypes.string,
};

export default ArtifactSummary;
